import React, { memo, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { IAlyceCustomTheme } from '../../styles/alyceTheme';
import { TCountry } from '../../store/countries/countries.types';

const useStyles = makeStyles<IAlyceCustomTheme, { image: string; available: boolean }>(
  ({ palette, spacing, breakpoints }) => ({
    product: {
      position: 'relative',
      textAlign: 'center',
      marginTop: spacing(4),
      [breakpoints.down('sm')]: {
        marginTop: 0,
      },
    },
    image: {
      position: 'relative',
      width: '100%',
      marginBottom: 10,
      borderRadius: 4,
      height: 320,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: ({ image }) => (image ? `url('${image}')` : ''),
      opacity: ({ available }) => (available ? 1 : 0.5),
      [breakpoints.down('sm')]: {
        height: 260,
      },
    },
    productName: {
      color: palette.text.primary,
      fontSize: 32,
      fontWeight: 700,
      lineHeight: '40px',
      wordBreak: 'break-word',
      wordWrap: 'break-word',
      fontFamily: 'Lato',
    },
    productBrand: {
      color: palette.grey.main,
      fontSize: 18,
      fontWeight: 400,
      lineHeight: '24px',
      fontFamily: 'Lato',
      marginBottom: spacing(1),
    },
    countryLabel: {
      fontSize: 16,
      fontFamily: 'Lato',
      marginRight: spacing(1),
      color: palette.text.primary,
      paddingBottom: spacing(1 / 8),
    },
    countryName: {
      color: palette.primary.main,
      fontSize: 16,
      fontWeight: 'bold',
      fontFamily: 'Lato',
      lineHeight: 1.5,
      marginLeft: spacing(0.5),
    },
  }),
);

export interface IProductPreviewProps {
  name: string;
  image: string;
  provider: string;
  country: TCountry;
  hasExternalFulfillment: boolean;
  available: boolean;
  unavailableText?: ReactNode;
}

const ProductPreview = ({
  name,
  image,
  provider,
  country,
  hasExternalFulfillment,
  available,
  unavailableText,
}: IProductPreviewProps): JSX.Element => {
  const classes = useStyles({ image, available });
  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Typography className={classes.countryLabel}>Available in: </Typography>
        <img width={16} height={16} src={country?.image} alt={`${country?.name} flag`} />{' '}
        <Typography className={classes.countryName}>{country?.name}</Typography>
      </Box>
      <Box className={classes.product}>
        <Box className={classes.image} />
        {available ? (
          <>
            <Box className={classes.productName}>{name}</Box>
            {!hasExternalFulfillment && <Box className={classes.productBrand}>{provider}</Box>}
          </>
        ) : (
          unavailableText
        )}
      </Box>
    </>
  );
};

export default memo(ProductPreview);
